import { ITimelineData } from './../interactivity-editor/redux/timeline-data.model';
import { SapiService } from 'app/services/sapi.service';
import { Publication } from 'app/interfaces/publication';
import { PublicationService } from 'app/services/publication.service';
import { Subscription, catchError, of } from 'rxjs';
import { TimeService } from './../../../services/time.service';
import { bbGenerateShortId } from 'app/shared/utils/bbGenerateShortId.util';
import { Component, ElementRef, forwardRef, Input, OnDestroy, AfterViewInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Mediaclip } from 'app/interfaces/mediaclip';
import { Debounce } from 'app/decorators/debounce.decorator';

export type ChapterList = ChapterListItem[];
export interface ChapterListItem {
	id: string;
	title: string;
	timeOffset: number;
}

@Component({
	selector: 'bb-chapter-editor',
	templateUrl: './bb-chapter-editor.component.html',
	styleUrls: ['./bb-chapter-editor.component.scss'],
	providers: [{
		provide: NG_VALUE_ACCESSOR,
		useExisting: forwardRef(() => BbChapterEditorComponent),
		multi: true
	}]
})
export class BbChapterEditorComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
	@Input() inModal = false;
	@Input() mediaclip: Mediaclip;

	modelValue: ChapterList = [];
	el: HTMLElement;
	playerApi: any;
	currentTime = 0;
	displayTime = '00:00';
	activeChapterId = null;
	previewAsset = null;
	subscriptions = new Subscription();
	defaultMediaAssetPath = '';
	highlightId = '';
	hasTimelineChapters = false;
	timeline: ITimelineData;
	loadingTimeline = true;
	isPlaying = false;
	doubleChapterTimeRef = {};

	constructor(
		private _eref: ElementRef,
		private publicationService: PublicationService,
		private sapiService: SapiService,
		private timeService: TimeService
	) {
		this.el = this._eref.nativeElement;
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

	ngAfterViewInit() {
		this.subscriptions.add(
			this.publicationService.getData().subscribe((publicationData: Publication) => {
				if (!publicationData) {
					return;
				}
				this.defaultMediaAssetPath = publicationData.defaultMediaAssetPath;
			})
		);
		// this.previewAsset = this.getPreviewAsset(); // might use later

		if (this.mediaclip?.timelines?.length) {
			this.getTimeline(this.mediaclip.timelines[0]?.id);
		} else {
			setTimeout(() => {
				this.loadingTimeline = false;
			}, 100);
		}
	}

	importFromInteractivity() {
		// TODO: remove the chapters from the timeline?
		this.hasTimelineChapters = false; // hack to see interface
		this.modelValue = this.timeline.widgets?.filter(w => w.widgetdefinitiontype === 'chapter')
			.map(w => {
				const timeOffset = parseFloat(this.timeline.order?.find(o => o.widgetid === w.id)?.inoutpoints[0].inpoint);
				return {
					id: w.id,
					title: w.label,
					timeOffset
				};
			});
		this.onChange();
	}

	getTimeline(timelineId) {
		if (!timelineId) {
			return;
		}
		this.loadingTimeline = true;
		this.sapiService.get('timeline', timelineId)
			.pipe(catchError((err) => of(null)))
			.subscribe((timeline: ITimelineData) => {
				setTimeout(() => {
					this.loadingTimeline = false;
				}, 1);

				this.timeline = timeline;
				this.hasTimelineChapters = timeline.widgets.some(w => w.widgetdefinitiontype === 'chapter');
			});
	}

	getPreviewAsset() {
		return this.mediaclip?.assets?.find(asset => asset.mediatype === 'MP4_PREVIEW');
	}

	@Debounce(50)
	onTimecodeInput(time?: number) {
		if (time && this.playerApi) {
			this.playerApi.seek(time);
		}
	}

	highlightChapter(chapter: ChapterListItem) {
		this.highlightId = chapter.id;
		setTimeout(() => {
			this.highlightId = '';
		}, 1000); // animation duration
	}

	onTitleEnter() {
		setTimeout(() => {
			this.addChapter();
		}, 1000);
	}

	@Debounce(5)
	onChange(time?: number) {
		if (time && this.playerApi) {
			this.playerApi.seek(time);
		}
		this.modelValue = this.modelValue.sort((a, b) => a.timeOffset - b.timeOffset);
		this.checkChapterTimes();
		this.propagateChange(this.modelValue);
		if (!this.hasTimelineChapters) {
			this.playerApi?.updateClipChapters?.(this.mediaclip);
		}
	}

	checkChapterTimes() {
		const chapterTimes = this.modelValue.map(c => Math.round(c.timeOffset));
		this.modelValue.forEach((chapter, i) => {
			const timeIndex = chapterTimes.indexOf(Math.round(chapter.timeOffset));
			this.doubleChapterTimeRef[chapter.id] = timeIndex > -1 && timeIndex !== i;
		});
	}

	setActiveChapter(chapter: ChapterListItem) {
		this.activeChapterId = chapter.id;
		this.playerApi?.seek(chapter.timeOffset);
	}

	trackByFn(index, item) {
		return item.id;
	}

	onPlayerCreated(api) {
		let played = false;
		this.playerApi = api;
		api.on('timeupdate seeking seeked phasechange', () => {
			this.currentTime = api.getCurrentTime();
			this.displayTime = this.timeService.secondsToTimeString(this.currentTime, false, null, false, false, true);
			if (!played && this.currentTime > 0 && api.getState() === 'playing') {
				setTimeout(() => {
					api.pause();
					api.seek(0);
				}, 1);
				played = true;
			}
		});
	}

	seekFrame(frameOffset: number) {
		const frameRate = 25;
		const frameTime = 1 / frameRate;
		const time = this.currentTime + frameOffset * frameTime;
		this.playerApi?.seek(time);
	}

	addChapter(atCurrentTime = false) {
		const clipDuration = this.mediaclip.length - 1;
		const maxTime = Math.max(...this.modelValue.map(c => c.timeOffset));
		const newTime = !this.modelValue.length && !atCurrentTime ?
			0 :
			atCurrentTime ?
				this.currentTime :
				Math.min(clipDuration, Math.round(maxTime) + 10);
		const newChapter = {
			id: bbGenerateShortId(),
			title: '',
			timeOffset: newTime
		};
		if (atCurrentTime && this.currentTime > 0.5 && !this.modelValue.length) {
			this.modelValue.push({
				id: bbGenerateShortId(),
				title: '',
				timeOffset: 0
			});
		}
		this.modelValue.push(newChapter);
		this.activeChapterId = newChapter.id;
		this.onChange();
	}

	removeChapter(index: number) {
		this.modelValue.splice(index, 1);
		this.onChange();
	}

	// ControlValueAccessor interface methods
	writeValue(value: any) {
		if (value !== this.modelValue) {
			this.modelValue = typeof value !== 'string' && value || [];
		}
	}

	propagateChange = (_: any) => { };

	registerOnChange(fn: any) {
		this.propagateChange = fn;
	}

	registerOnTouched() {

	}
}
